"use client"

import { useState, useCallback } from "react"
import { MessageCircle } from "lucide-react"
import { CommentForm } from "./CommentForm"
import { CommentCard } from "./CommentCard"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

// Helper to extract date from Firestore Timestamp or ISO string
function getDate(value: unknown): Date {
  if (!value) return new Date()
  if (typeof value === "string") return new Date(value)
  if (value instanceof Date) return value
  if (typeof value === "object" && "toDate" in value && typeof (value as { toDate: () => Date }).toDate === "function") {
    return (value as { toDate: () => Date }).toDate()
  }
  return new Date()
}

interface Comment {
  id: string
  authorId: string
  content: string
  upvoteCount?: number
  downvoteCount?: number
  isEdited?: boolean
  createdAt?: unknown
  author?: {
    username?: string
    displayName?: string
    avatarUrl?: string
  }
}

interface CommentSectionProps {
  postId: string
  comments: Comment[]
  commentCount: number
  onRefresh?: () => void
}

type SortOption = "newest" | "oldest" | "top"

export function CommentSection({ postId, comments, commentCount, onRefresh }: CommentSectionProps) {
  const [sortBy, setSortBy] = useState<SortOption>("newest")

  const sortedComments = [...comments].sort((a, b) => {
    switch (sortBy) {
      case "newest":
        return getDate(b.createdAt).getTime() - getDate(a.createdAt).getTime()
      case "oldest":
        return getDate(a.createdAt).getTime() - getDate(b.createdAt).getTime()
      case "top":
        return (b.upvoteCount || 0) - (b.downvoteCount || 0) - ((a.upvoteCount || 0) - (a.downvoteCount || 0))
      default:
        return 0
    }
  })

  const handleCommentSuccess = useCallback(() => {
    onRefresh?.()
  }, [onRefresh])

  const handleDeleteComment = useCallback(
    (commentId: string) => {
      onRefresh?.()
    },
    [onRefresh],
  )

  return (
    <section id="comments" className="mt-12">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h2 className="flex items-center text-xl font-bold text-gray-900 dark:text-white">
          <MessageCircle className="mr-2 h-5 w-5" />
          Comments ({commentCount})
        </h2>
        <Select value={sortBy} onValueChange={value => setSortBy(value as SortOption)}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="oldest">Oldest</SelectItem>
            <SelectItem value="top">Top</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Comment Form */}
      <div className="mb-8">
        <CommentForm postId={postId} onSuccess={handleCommentSuccess} />
      </div>

      {/* Comments List */}
      {sortedComments.length > 0 ? (
        <div className="divide-y divide-gray-200 dark:divide-gray-800">
          {sortedComments.map(comment => (
            <CommentCard key={comment.id} comment={comment} postId={postId} onDelete={handleDeleteComment} />
          ))}
        </div>
      ) : (
        <div className="py-12 text-center">
          <MessageCircle className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No comments yet</h3>
          <p className="mt-2 text-gray-500 dark:text-gray-400">Be the first to share your thoughts!</p>
        </div>
      )}
    </section>
  )
}
